import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { BRAND_LOGO, HERO_IMAGES, GALLERY_IMAGES } from "../lib/brand";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import MerchantCard from "../components/brand/MerchantCard";
import PopupAdManager from "../components/brand/PopupAdManager";
import { ScanSmiley, IdentificationBadge, Champagne, MusicNote, ShieldStar, ChartLineUp } from "@phosphor-icons/react";

export default function Landing() {
  const [merchants, setMerchants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    api.get("/merchants").then(({ data }) => setMerchants(data));
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/events").then(({ data }) => setEvents(data));
  }, []);

  const featured = merchants.filter((m) => m.featured).slice(0, 6);
  const filtered = activeCat ? merchants.filter((m) => m.category_id === activeCat) : merchants;

  return (
    <div className="min-h-screen bg-[#050505] relative">
      <PopupAdManager placement="landing" />
      <BrandHeader />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES[0]} alt="" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-[#050505]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl rise-in">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">A Private Members Network</span>
            </div>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight">
              Where the city's <span className="italic text-gold-gradient">finest evenings</span> begin.
            </h1>
            <p className="mt-6 text-white/70 text-base sm:text-lg max-w-xl leading-relaxed">
              The Social Circle by Cornerstone is a private, members-only nightlife and dining network — connecting you to the venues, the events, and the people who define the night.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/register" className="tsc-btn-gold text-center" data-testid="hero-cta-join">Join the Circle</Link>
              <Link to="/partners" className="tsc-btn-outline text-center" data-testid="hero-cta-partners">Browse Partner Venues</Link>
            </div>
            <p className="mt-6 italic text-white/40 text-sm">"Bringing people and venues together."</p>
          </div>
        </div>
      </section>

      {/* SCAN. REGISTER. ENJOY. — branded section inspired by reference */}
      <section className="relative max-w-7xl mx-auto px-6 py-20 sm:py-28" data-testid="scan-register-enjoy-section">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="divider-dot text-[10px] tracking-luxe uppercase max-w-xs"><span>The Method</span></div>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl text-white leading-[1.05]">
              <span className="text-gold-gradient">Scan.</span> <span className="text-white">Register.</span> <span className="italic text-white">Enjoy.</span>
            </h2>
            <p className="mt-5 text-white/65 text-base max-w-lg leading-relaxed">
              Discover a partner venue. Register your visit in seconds. Receive your branded Social Circle code — and unlock exclusive privileges curated for the inner circle.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-5 max-w-xl">
              {[
                { icon: <ScanSmiley size={20} weight="duotone" />, title: "Scan", text: "Tap or scan at any partner venue." },
                { icon: <IdentificationBadge size={20} weight="duotone" />, title: "Register", text: "Quick form. Branded TSC referral code." },
                { icon: <Champagne size={20} weight="duotone" />, title: "Show & enjoy", text: "Present your code, unlock the discount." },
                { icon: <ChartLineUp size={20} weight="duotone" />, title: "We track", text: "Every visit supports more curated nights." },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`method-step-${i+1}`}>
                  <div className="h-9 w-9 rounded-full bg-[#0d0d0d] border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center flex-shrink-0">{s.icon}</div>
                  <div>
                    <p className="text-white text-sm font-medium">{s.title}</p>
                    <p className="text-white/55 text-xs mt-0.5">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <Link to="/partners" className="tsc-btn-gold" data-testid="scan-section-cta">Find a venue near you</Link>
            </div>
          </div>

          {/* QR-style mock card */}
          <div className="relative">
            <div className="absolute inset-0 -z-10 blur-3xl opacity-30 bg-gradient-to-br from-[#D4AF37] to-transparent" />
            <div className="qr-frame max-w-md mx-auto p-8 sm:p-10 rounded-md text-center grain glow-pulse">
              <div className="flex flex-col items-center gap-2">
                <img src={BRAND_LOGO} alt="" className="h-16 w-16 rounded-full ring-1 ring-[#D4AF37]/40" />
                <p className="font-display text-2xl text-white tracking-wide">THE SOCIAL CIRCLE</p>
                <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">by Cornerstone</p>
              </div>
              <p className="mt-6 font-display text-3xl text-white">SCAN. REGISTER. ENJOY.</p>
              <p className="text-white/60 text-sm mt-2">Exclusive privileges for partner venues.</p>

              <div className="bg-white p-3 rounded mx-auto inline-block mt-6">
                <div className="grid grid-cols-9 grid-rows-9 gap-px w-44 h-44">
                  {Array.from({ length: 81 }).map((_, i) => {
                    const r = Math.random();
                    return <div key={i} className={r > 0.55 ? "bg-black" : "bg-white"} />;
                  })}
                </div>
              </div>

              <div className="mt-3 inline-block bg-gold-gradient text-black font-semibold tracking-luxe text-xs px-4 py-1.5 rounded-full">
                MEMBER PRIVILEGES UNLOCKED
              </div>
              <div className="mt-4 flex items-center justify-center gap-2">
                <div className="h-px w-10 bg-[#D4AF37]/40" />
                <div className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                <div className="h-px w-10 bg-[#D4AF37]/40" />
              </div>
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]/80 mt-2">A sample of your branded code</p>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY FILTER + GALLERY */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="merchant-gallery">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Curated Partners</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white leading-tight">The City's Finest, in one Circle.</h2>
          </div>
          <Link to="/partners" className="text-[#D4AF37] text-[11px] tracking-luxe uppercase hover:text-[#F9E596]" data-testid="see-all-partners">See all →</Link>
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-8" data-testid="category-filters">
          <button onClick={() => setActiveCat(null)}
            className={`text-[10px] tracking-luxe uppercase px-4 py-2 rounded-full border whitespace-nowrap transition-colors ${activeCat === null ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70 hover:border-[#D4AF37]/50"}`}
            data-testid="cat-filter-all">All</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setActiveCat(c.id)}
              className={`text-[10px] tracking-luxe uppercase px-4 py-2 rounded-full border whitespace-nowrap transition-colors ${activeCat === c.id ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70 hover:border-[#D4AF37]/50"}`}
              data-testid={`cat-filter-${c.slug}`}>{c.name}</button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 rounded-lg" data-testid="no-merchants">
            <p className="text-white/50 italic">The Circle is being curated. New partner venues arriving soon.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.slice(0, 12).map((m) => <MerchantCard key={m.id} merchant={m} />)}
          </div>
        )}
      </section>

      {/* FEATURED PARTNERS strip */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="featured-partners">
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Featured</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-white">Tonight's Spotlight</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {featured.map((m) => <MerchantCard key={m.id} merchant={m} />)}
          </div>
        </section>
      )}

      {/* EVENTS */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="events-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">The Calendar</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white leading-tight">Upcoming Nights</h2>
          </div>
          <Link to="/events" className="text-[#D4AF37] text-[11px] tracking-luxe uppercase hover:text-[#F9E596]">All events →</Link>
        </div>
        {events.length === 0 ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[0,1,2].map((i) => (
              <div key={i} className="tsc-card overflow-hidden">
                <div className="aspect-[4/3] bg-[#0a0a0a] relative">
                  <img src={GALLERY_IMAGES[i]} alt="" className="w-full h-full object-cover opacity-70" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Coming soon</p>
                    <p className="font-display text-2xl text-white mt-1">Acoustic Nights · {["Manila", "Cebu", "Boracay"][i]}</p>
                    <p className="text-white/60 text-sm mt-1">Curated live performances at partner venues.</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {events.slice(0, 6).map((e) => (
              <Link to="/events" key={e.id} className="tsc-card overflow-hidden block group" data-testid={`event-card-${e.id}`}>
                <div className="aspect-[4/3] bg-[#0a0a0a] relative">
                  {e.cover_url && <img src={e.cover_url} alt={e.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">{new Date(e.starts_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
                    <p className="font-display text-2xl text-white mt-1 leading-tight">{e.title}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* MEMBER PERKS */}
      <section className="relative py-20" data-testid="perks-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={GALLERY_IMAGES[1]} alt="" className="rounded-lg w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-[#0d0d0d] border border-[#D4AF37]/40 rounded-md p-5 max-w-[260px]">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Inside the Circle</p>
              <p className="font-display text-xl text-white mt-2 leading-tight">Privileges built around your evenings.</p>
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Membership</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white leading-tight">More than a discount. <br /><span className="italic text-gold-gradient">A way of moving through the night.</span></h2>
            <ul className="mt-8 space-y-4 max-w-lg">
              {[
                { icon: <ShieldStar size={20} weight="duotone" />, title: "Permanent membership QR", text: "Recurring privileges across every partner venue." },
                { icon: <Champagne size={20} weight="duotone" />, title: "Members-only privileges", text: "Curated discounts and partner access for active members." },
                { icon: <MusicNote size={20} weight="duotone" />, title: "Priority event access", text: "Reserved tables, live acoustic nights, exclusive openings." },
              ].map((p, i) => (
                <li key={i} className="flex gap-4">
                  <div className="h-10 w-10 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center flex-shrink-0 bg-[#0a0a0a]">{p.icon}</div>
                  <div>
                    <p className="text-white font-medium">{p.title}</p>
                    <p className="text-white/55 text-sm mt-0.5">{p.text}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/register" className="tsc-btn-gold text-center" data-testid="perks-cta-register">Become a Member</Link>
              <Link to="/membership" className="tsc-btn-outline text-center" data-testid="perks-cta-learn">Learn more</Link>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNER WITH US */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="partner-cta">
        <div className="relative overflow-hidden rounded-md border border-[#D4AF37]/30 p-10 sm:p-16 text-center">
          <img src={HERO_IMAGES[1]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 to-[#050505]" />
          <div className="relative">
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">For Venues</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white leading-tight">Become a Social Circle Partner.</h2>
            <p className="text-white/65 mt-4 max-w-xl mx-auto">Join a private network of curated venues. Every visit tracked, every transaction transparent — built for nightlife operators.</p>
            <Link to="/partner-with-us" className="tsc-btn-gold inline-block mt-8" data-testid="partner-cta-button">Apply to Partner</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
